import React, { useRef, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import type { ReactNode } from 'react';
import { useRouter } from 'next/router';

const useIsActiveLink = (props: Pick<IProps, 'href' | 'alias'>) => {
  const { pathname } = useRouter();
  return useMemo(
    () => [props.href, ...(props.alias || [])].some((x) => x === pathname),
    [props.href, props.alias, pathname],
  );
};

const ActiveLink = forwardRef<any, IProps>(
  ({ children, className: clz, activeClassName = 'active', ...props }, ref) => {
    const router = useRouter();
    const isActive = useIsActiveLink(props);
    // 防止外部传入 ref 报错
    const childRef = useRef<any>();
    useImperativeHandle(ref, () => childRef.current);

    const onLinkClick = useCallback(() => {
      if (isActive) return;
      if (props.isInApp) {
        if (props.replace !== false) {
          router.replace(props.href);
        } else {
          router.push(props.href);
        }
        return;
      }
      if (props.replace !== false) {
        window.location.replace(props.href);
      } else {
        window.location.href = props.href;
      }
    }, [props, isActive]);

    return (
      <span {...props} onClick={onLinkClick}>
        <div className={classnames(clz, { [activeClassName]: isActive })} ref={childRef}>
          {children}
        </div>
      </span>
    );
  },
);
ActiveLink.displayName = 'ActiveLink';

interface IProps {
  className?: string;
  activeClassName?: string;
  children: ReactNode;
  href: string;
  /** 用replace而不是push/goto */
  replace?: boolean;
  /** 是否是应用内, 如果是则使用 router.push */
  isInApp?: boolean;
  /** 别名, 用于高亮. 例如 [/home, /index] 都会使此链接高亮 */
  alias?: string[];
}

export default ActiveLink;
